// <styling-booking> — custom booking calendar for the Styling page (Figma 687:1773).
// Fully interactive UI. Owns all client state and re-renders the month grid, time
// slots and the live summary panel on every change.
//
// Availability and booking go through getAvailableSlots() and submitBooking(),
// which call a Cal.com proxy hosted on Vercel (holds the Cal config server-side).
// Override the proxy origin per-instance with data-proxy-base on the element.
// Follows Dawn's custom element pattern.

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_PROXY_BASE = 'https://ella-rue-booking-proxy.vercel.app';

// Pure helper: build the cells for a month grid.
// Returns an array of { day: number|null }, with leading nulls for the
// weekday offset of the 1st. Exported behavior verified in the console check below.
function buildMonthCells(year, month) {
  const firstWeekday = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push({ day: null });
  for (let d = 1; d <= daysInMonth; d += 1) cells.push({ day: d });
  return cells;
}

function startOfToday() {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

// Format a Date's local calendar day as YYYY-MM-DD (no timezone shift — the Date
// is built from calendar components, so we read those back directly).
function toISODate(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

class StylingBooking extends HTMLElement {
  constructor() {
    super();
    const today = startOfToday();
    this.proxyBase = this.dataset.proxyBase || DEFAULT_PROXY_BASE;
    this.slotToken = 0; // guards against out-of-order slot fetches
    this.monthToken = 0; // same, for the month's available-days fetch
    this.availableDays = null; // Set of 'YYYY-MM-DD' with open slots, for the viewed month
    this.state = {
      type: '', // nothing selected by default — the visitor chooses everything
      location: '',
      locationKind: '', // 'in_person' | 'virtual', from the chosen Location pill
      year: today.getFullYear(),
      month: today.getMonth(),
      selectedDate: null, // Date or null
      selectedStart: null, // ISO string of the chosen slot, or null
      selectedTime: null, // display label e.g. "10:00 AM", or null
    };
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.slotsEl = this.querySelector('[data-slots]');
    this.monthLabel = this.querySelector('[data-month-label]');
    this.timesLabel = this.querySelector('[data-times-label]');
    this.timesLabelPrefix = this.timesLabel ? this.timesLabel.textContent.trim() : 'Available Times';
    this.form = this.querySelector('[data-form]');
    this.formError = this.querySelector('[data-form-error]');
    this.formSuccess = this.querySelector('[data-form-success]');
    // Parse comma-separated weekday indices (0=Sun … 6=Sat) that are always closed.
    // These grey out immediately — no proxy round-trip needed.
    const raw = this.dataset.closedWeekdays || '';
    this.closedWeekdays = new Set(raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n)));

    // No date is pre-selected — the visitor picks a day, then a time.
    this.bindEvents();
    this.loadMonth();
    this.renderSlots();
    this.renderSummary();
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const pill = event.target.closest('.styling-booking__pill');
      if (pill) return this.onPill(pill);
      const nav = event.target.closest('[data-nav]');
      if (nav) return this.onNav(nav.dataset.nav);
      const day = event.target.closest('.styling-booking__day');
      if (day && !day.classList.contains('is-empty') && !day.classList.contains('is-disabled')) {
        return this.onDay(Number(day.dataset.day));
      }
      const slot = event.target.closest('.styling-booking__slot');
      if (slot && !slot.classList.contains('is-disabled')) return this.onSlot(slot);
    });

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }
  }

  // ---- Data layer (Cal.com via Vercel proxy) ----
  // Returns [{ start: ISO, label: "10:00 AM" }] for the given day, or [] on error.
  async getAvailableSlots({ date }) {
    if (!date || date < startOfToday()) return [];
    try {
      const res = await fetch(`${this.proxyBase}/api/slots?date=${toISODate(date)}`);
      if (!res.ok) return [];
      const body = await res.json();
      return Array.isArray(body.slots) ? body.slots : [];
    } catch (err) {
      return [];
    }
  }

  // Returns the array of 'YYYY-MM-DD' in the given month that have open slots.
  async getAvailableDays(year, month) {
    const start = toISODate(new Date(year, month, 1));
    const end = toISODate(new Date(year, month + 1, 0));
    try {
      const res = await fetch(`${this.proxyBase}/api/days?start=${start}&end=${end}`);
      if (!res.ok) return [];
      const body = await res.json();
      return Array.isArray(body.days) ? body.days : [];
    } catch (err) {
      return [];
    }
  }

  submitBooking(payload) {
    return fetch(`${this.proxyBase}/api/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .catch(() => ({ ok: false }));
  }

  // ---- Selection helpers ----
  onPill(pill) {
    const group = pill.closest('[data-group]').dataset.group; // 'type' | 'location'
    this.state[group] = pill.dataset.value;
    if (group === 'location') this.state.locationKind = pill.dataset.kind || '';
    pill.parentElement.querySelectorAll('.styling-booking__pill')
      .forEach((p) => p.classList.toggle('is-selected', p === pill));
    this.renderSummary();
  }

  onNav(direction) {
    let { year, month } = this.state;
    month += direction === 'next' ? 1 : -1;
    if (month < 0) { month = 11; year -= 1; }
    if (month > 11) { month = 0; year += 1; }
    // Do not navigate to fully-past months (before the current month).
    const today = startOfToday();
    if (year < today.getFullYear() || (year === today.getFullYear() && month < today.getMonth())) return;
    this.state.year = year;
    this.state.month = month;
    // Changing month clears any prior date/time pick — nothing stays selected.
    this.state.selectedDate = null;
    this.state.selectedStart = null;
    this.state.selectedTime = null;
    this.loadMonth();
    this.renderSlots();
    this.renderSummary();
  }

  onDay(day) {
    this.state.selectedDate = new Date(this.state.year, this.state.month, day);
    this.state.selectedStart = null;
    this.state.selectedTime = null;
    this.renderMonth();
    this.renderSlots();
    this.renderSummary();
  }

  onSlot(slotEl) {
    this.state.selectedStart = slotEl.dataset.start;
    this.state.selectedTime = slotEl.dataset.label;
    this.slotsEl.querySelectorAll('.styling-booking__slot')
      .forEach((s) => s.classList.toggle('is-selected', s === slotEl));
    this.renderSummary();
  }

  // ---- Rendering ----
  // Fetch which days of the viewed month have open slots, then render the grid.
  // A gold spinner shows in the grid while the month loads.
  async loadMonth() {
    this.monthLabel.textContent = `${MONTH_NAMES[this.state.month]} ${this.state.year}`;
    this.grid.innerHTML =
      '<span class="styling-booking__grid-loading"><span class="styling-booking__spinner" role="status" aria-label="Loading availability"></span></span>';
    const token = (this.monthToken += 1);
    const days = await this.getAvailableDays(this.state.year, this.state.month);
    if (token !== this.monthToken) return; // a newer month load superseded this one
    this.availableDays = new Set(days);
    this.renderMonth();
  }

  renderMonth() {
    this.monthLabel.textContent = `${MONTH_NAMES[this.state.month]} ${this.state.year}`;
    const cells = buildMonthCells(this.state.year, this.state.month);
    const today = startOfToday();
    const sel = this.state.selectedDate;
    this.grid.innerHTML = cells.map((cell) => {
      if (cell.day == null) return '<span class="styling-booking__day is-empty"></span>';
      const d = new Date(this.state.year, this.state.month, cell.day);
      const isPast = d < today;
      const alwaysClosed = this.closedWeekdays.has(d.getDay());
      // Grey out days with no open slots (closed or fully booked) so they can't be picked.
      const unavailable = alwaysClosed || (this.availableDays && !this.availableDays.has(toISODate(d)));
      const isSel = sel && d.getTime() === sel.getTime();
      const cls = ['styling-booking__day'];
      if (isPast || unavailable) cls.push('is-disabled');
      if (isSel) cls.push('is-selected');
      return `<button type="button" class="${cls.join(' ')}" data-day="${cell.day}">${cell.day}</button>`;
    }).join('');
  }

  async renderSlots() {
    // No day chosen yet — prompt the visitor to pick one, don't fetch.
    if (!this.state.selectedDate) {
      this.timesLabel.textContent = this.timesLabelPrefix;
      this.slotsEl.innerHTML = '<p class="styling-booking__slots-note">Select a date to see available times.</p>';
      return;
    }

    const d = this.state.selectedDate;
    this.timesLabel.textContent =
      `${this.timesLabelPrefix} — ${WEEKDAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;

    // Availability is fetched over the network; ignore results from a stale
    // request if the user changed day/month while it was in flight.
    const token = (this.slotToken += 1);
    this.slotsEl.innerHTML =
      '<p class="styling-booking__slots-note"><span class="styling-booking__spinner" role="status" aria-label="Loading available times"></span></p>';
    const slots = await this.getAvailableSlots({ date: this.state.selectedDate });
    if (token !== this.slotToken) return;

    // No auto-selection — the visitor taps the time they want.
    this.slotsEl.innerHTML = slots.length
      ? slots.map((s) => {
        const isSel = s.start === this.state.selectedStart;
        return `<button type="button" class="styling-booking__slot${isSel ? ' is-selected' : ''}" data-start="${s.start}" data-label="${s.label}">${s.label}</button>`;
      }).join('')
      : '<p class="styling-booking__slots-note">No times available this day.</p>';
    this.renderSummary();
  }

  renderSummary() {
    const setText = (sel, text) => {
      const el = this.querySelector(sel);
      if (el) el.textContent = text;
    };
    setText('[data-summary-type]', this.state.type || '—');
    setText('[data-summary-location]', this.state.location || '—');
    if (this.state.selectedDate && this.state.selectedTime) {
      const d = this.state.selectedDate;
      setText('[data-summary-when]',
        `${WEEKDAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()} at ${this.state.selectedTime}`);
    } else {
      setText('[data-summary-when]', '—');
    }
  }

  // ---- Submit ----
  onSubmit(event) {
    event.preventDefault();
    this.formError.hidden = true;
    this.formSuccess.hidden = true;

    const data = new FormData(this.form);
    const fail = (message) => {
      this.formError.hidden = false;
      this.formError.textContent = message;
    };
    if (!this.state.type || !this.state.location) {
      fail('Please choose an appointment type and location.');
      return;
    }
    if (!this.state.selectedStart) {
      fail('Please pick a date and time.');
      return;
    }
    const missing = ['first_name', 'last_name', 'email'].filter((k) => !String(data.get(k) || '').trim());
    if (missing.length) {
      fail('Please fill in your first name, last name and email.');
      return;
    }

    const payload = {
      type: this.state.type,
      location: this.state.location,
      locationKind: this.state.locationKind,
      start: this.state.selectedStart,
      firstName: data.get('first_name'),
      lastName: data.get('last_name'),
      email: data.get('email'),
      phone: data.get('phone'),
      notes: data.get('notes'),
      company: data.get('company'), // honeypot — real visitors leave this empty
    };

    // Show a gold spinner only while the request is actually in flight, then
    // reveal the confirmation as soon as it resolves — no artificial delay.
    const confirmBtn = this.form.querySelector('.styling-booking__confirm');
    if (confirmBtn) confirmBtn.disabled = true;
    this.formSuccess.hidden = false;
    this.formSuccess.innerHTML =
      '<span class="styling-booking__spinner" role="status" aria-label="Submitting your request"></span>';

    this.submitBooking(payload).then((res) => {
      if (confirmBtn) confirmBtn.disabled = false;
      if (res && res.ok) {
        this.form.reset();
        this.formSuccess.textContent = 'Your appointment is confirmed. Check your email for the details.';
      } else {
        this.formSuccess.hidden = true;
        this.formError.hidden = false;
        this.formError.textContent = 'Something went wrong. Please call us to book.';
      }
    });
  }
}

customElements.define('styling-booking', StylingBooking);
